/**
 * TerraMatrix Universal Data Platform - DataSourceDetailPage
 *
 * Shows data source details with list of tables/collections.
 * Users can edit table descriptions and drill down to field-level editing.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Database,
  Table2,
  Loader2,
  Edit2,
  Check,
  X,
  ChevronRight,
  Sparkles,
  RefreshCw,
  Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { dataApi, DataSource } from '@/services/api/dataApi';
import { useToast } from '@/hooks/use-toast';

interface TableInfo {
  name: string;
  documentCount: number;
  fieldCount: number;
  description?: string;
  purpose?: string;
  aiDescription?: string;
  fields?: Record<string, any>;
}

export const DataSourceDetailPage: React.FC = () => {
  const { sourceId } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  // State
  const [dataSource, setDataSource] = useState<DataSource | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Editing state
  const [editingTableName, setEditingTableName] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState('');
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [isGeneratingAI, setIsGeneratingAI] = useState<string | null>(null);

  // Load data source and schema
  const loadDataSource = useCallback(async () => {
    if (!sourceId) return;

    setIsLoading(true);
    setError(null);

    try {
      // Get data source details
      const source = await dataApi.getSource(sourceId);
      setDataSource(source);

      // Get schema/tables from the source's schema property or fetch fresh
      if (source.schema?.tables && source.schema.tables.length > 0) {
        const tableInfos: TableInfo[] = source.schema.tables.map((t: any) => ({
          name: t.name,
          documentCount: t.rowCount || 0,
          fieldCount: t.fields?.length || 0,
          description: t.description,
          purpose: t.purpose,
          fields: t.fields,
        }));
        setTables(tableInfos);
      } else {
        // No schema yet - need to discover
        setTables([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data source');
    } finally {
      setIsLoading(false);
    }
  }, [sourceId]);

  useEffect(() => {
    loadDataSource();
  }, [loadDataSource]);

  // Refresh schema discovery
  // Note: Schema discovery feature temporarily unavailable (analytics-backend removed)
  const handleRefreshSchema = async () => {
    toast({
      title: 'Feature Unavailable',
      description: 'Schema refresh is temporarily unavailable.',
      variant: 'destructive',
    });
  };

  // Start editing a table description
  const startEditingTable = (tableName: string, currentDescription?: string) => {
    setEditingTableName(tableName);
    setEditDescription(currentDescription || '');
  };

  // Save table description
  const saveTableDescription = async () => {
    if (!editingTableName || !sourceId || !dataSource) return;

    setIsSavingDescription(true);

    try {
      // Update the table in local state
      const updatedTables = tables.map((t) =>
        t.name === editingTableName ? { ...t, description: editDescription } : t
      );
      setTables(updatedTables);

      // Update on server
      const schemaTables = updatedTables.map((t) => ({
        name: t.name,
        rowCount: t.documentCount,
        fields: t.fields
          ? Object.entries(t.fields).map(([name, info]: [string, any]) => ({
              name,
              type: Array.from(info.types || [])[0] || 'unknown',
            }))
          : [],
        description: t.description || t.purpose,
      }));

      await dataApi.updateSource(sourceId, {
        schema: { tables: schemaTables },
      });

      toast({
        title: 'Description Saved',
        description: `Updated description for "${editingTableName}".`,
      });

      setEditingTableName(null);
      setEditDescription('');
    } catch (err) {
      toast({
        title: 'Save Failed',
        description: err instanceof Error ? err.message : 'Failed to save description',
        variant: 'destructive',
      });
    } finally {
      setIsSavingDescription(false);
    }
  };

  // Generate AI description for a table
  const generateAIDescription = async (tableName: string) => {
    setIsGeneratingAI(tableName);

    try {
      const table = tables.find((t) => t.name === tableName);
      if (!table) return;

      // Call AI endpoint (or use local fallback)
      const response = await fetch('/api/analytics/ai/describe-table', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tableName,
          fieldNames: table.fields ? Object.keys(table.fields) : [],
          documentCount: table.documentCount,
          existingPurpose: table.purpose,
        }),
      });

      let description: string;
      if (response.ok) {
        const data = await response.json();
        description = data.description;
      } else {
        // Fallback local generation
        description = generateLocalTableDescription(table);
      }

      // Update table with AI description and start editing
      const updatedTables = tables.map((t) =>
        t.name === tableName ? { ...t, aiDescription: description } : t
      );
      setTables(updatedTables);

      // Auto-fill the edit field
      startEditingTable(tableName, description);
    } catch {
      // Fallback
      const table = tables.find((t) => t.name === tableName);
      if (table) {
        const description = generateLocalTableDescription(table);
        startEditingTable(tableName, description);
      }
    } finally {
      setIsGeneratingAI(null);
    }
  };

  // Local fallback for table description
  const generateLocalTableDescription = (table: TableInfo): string => {
    const name = table.name;
    const docCount = table.documentCount;
    const fieldCount = table.fieldCount;

    // Try to infer purpose from name
    const lowerName = name.toLowerCase();
    let purpose = '';

    if (lowerName.includes('moisture')) {
      purpose = 'Soil moisture sensor readings and measurements.';
    } else if (lowerName.includes('water') && lowerName.includes('level')) {
      purpose = 'Water level monitoring data from tanks or reservoirs.';
    } else if (lowerName.includes('history') || lowerName.includes('histories')) {
      purpose = `Historical data records for ${name.replace(/histor(y|ies)/i, '').trim() || 'tracking'}.`;
    } else if (lowerName.includes('device') || lowerName.includes('sensor')) {
      purpose = 'Device/sensor configuration and metadata.';
    } else if (lowerName.includes('raw')) {
      purpose = 'Raw data storage for debugging and analysis.';
    } else {
      purpose = `Collection containing ${docCount.toLocaleString()} documents with ${fieldCount} fields.`;
    }

    return purpose;
  };

  // Navigate to field editor
  const navigateToFields = (tableName: string) => {
    navigate(`/data/sources/${sourceId}/tables/${encodeURIComponent(tableName)}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !dataSource) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto py-8 px-4">
          <Button variant="ghost" onClick={() => navigate('/data')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Data
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{error || 'Data source not found'}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Header */}
        <div className="mb-6">
          <Button variant="ghost" onClick={() => navigate('/data')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Data
          </Button>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Database className="w-6 h-6" />
                {dataSource.name}
              </h1>
              <p className="text-muted-foreground capitalize">{dataSource.type}</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleRefreshSchema} disabled={isRefreshing}>
                {isRefreshing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh Schema
              </Button>
              <Button variant="outline">
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </Button>
            </div>
          </div>
        </div>

        {/* Data Source Info Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Data Source Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <Badge variant={dataSource.status === 'active' ? 'default' : 'secondary'}>
                  {dataSource.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tables/Collections</p>
                <p className="font-medium">{tables.length}</p>
              </div>
              <div className="col-span-2">
                <p className="text-sm text-muted-foreground">Description</p>
                <p className="text-sm">
                  {dataSource.metadata?.description || 'No description provided'}
                </p>
              </div>
              {dataSource.metadata?.tags && dataSource.metadata.tags.length > 0 && (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground mb-1">Tags</p>
                  <div className="flex flex-wrap gap-1">
                    {dataSource.metadata.tags.map((tag: string) => (
                      <Badge key={tag} variant="outline">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tables List */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Table2 className="w-5 h-5" />
              Tables / Collections
            </CardTitle>
            <CardDescription>
              Click on a table to view and edit field definitions. Use AI to generate descriptions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tables.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">
                  No tables discovered yet. Click "Refresh Schema" to discover tables.
                </p>
                <Button onClick={handleRefreshSchema} disabled={isRefreshing}>
                  {isRefreshing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Discover Tables
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tables.map((table) => (
                  <div
                    key={table.name}
                    className="border rounded-lg p-4 hover:border-primary/50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-lg">{table.name}</h3>
                          <Badge variant="outline" className="text-xs">
                            {table.fieldCount} fields
                          </Badge>
                        </div>

                        {/* Description (editing or display) */}
                        {editingTableName === table.name ? (
                          <div className="mt-2 space-y-2">
                            <Textarea
                              value={editDescription}
                              onChange={(e) => setEditDescription(e.target.value)}
                              placeholder="Describe what this table contains..."
                              rows={2}
                              className="text-sm"
                            />
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                onClick={saveTableDescription}
                                disabled={isSavingDescription}
                              >
                                {isSavingDescription ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Check className="w-3 h-3 mr-1" />
                                )}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setEditingTableName(null);
                                  setEditDescription('');
                                }}
                              >
                                <X className="w-3 h-3 mr-1" />
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-base mt-2 text-foreground/80">
                            {table.description || table.purpose || (
                              <span className="text-muted-foreground italic">No description - click AI or Edit to add one</span>
                            )}
                          </p>
                        )}
                      </div>

                      {/* Actions */}
                      {editingTableName !== table.name && (
                        <div className="flex items-center gap-1 ml-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => generateAIDescription(table.name)}
                            disabled={isGeneratingAI === table.name}
                            title="Generate AI description"
                          >
                            {isGeneratingAI === table.name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Sparkles className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEditingTable(table.name, table.description)}
                            title="Edit description"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigateToFields(table.name)}
                            title="View fields"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataSourceDetailPage;
